import React, { useEffect, useState, useCallback, useMemo, memo } from "react";
import {
  DeviceEventEmitter,
  Dimensions,
  Platform,
  Pressable,
  StyleSheet,
} from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  runOnJS,
} from "react-native-reanimated";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useTheme } from "styled-components/native";
import Routes from "@navigations/Routes";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useTypographyStore } from "@redux/slices/typographySlice";
import {
  AIEventNames,
  AIEventTypes,
} from "@scenes/authentication/utils/Types/types";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import { useCoachOverlay } from "../../../hooks/useCoachOverlay";
import { CoachOverlayStep } from "@components/CoachOverlay";
import { AISearchButtonVisual } from "@components/atoms/AISearchFloatingButton/AISearchButtonVisual";
import { ChatTooltip } from "@components/atoms/AISearchFloatingButton/ChatToolTip";

const BUTTON_HEIGHT = 48;
const EDGE_MARGIN = 8;

let savedPosition: { x: number; y: number } | null = null;

// Pure JS functions for runOnJS - defined outside component
const savePositionJS = (x: number, y: number) => {
  savedPosition = { x, y };
};

type FloatingChatBotButtonProps = {
  isDraggable?: boolean;
  fixedBottomMargin?: number;
  fixedRightMargin?: number;
  routeName: string;
};

const FloatingChatBotButton = ({
  isDraggable = true,
  fixedBottomMargin = Platform.OS === "ios" ? 110 : 78,
  fixedRightMargin = 50,
}: FloatingChatBotButtonProps) => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const userInfo = useUserInfo();
  const orgSettings = useOrgSettingsSlice();
  const theme = useTheme();
  const isWeb = theme.isLargeScreen;
  const typography = useTypographyStore();
  const BUTTON_WIDTH = theme.isLargeScreen ? 181 : 167;
  const { trackEvent } = useUserAnalytics();

  const shouldRender = useMemo(() => {
    return !!userInfo.user.id;
  }, [userInfo.user.id, orgSettings.settings.organization.id]);

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [currentPosition, setCurrentPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const { screenConfig } = useCoachOverlay({
    screen: "AISearchWidget",
    autoStart: true,
    delayMs: 200,
  });

  useEffect(() => {
    const openAISearchWidget = DeviceEventEmitter.addListener(
      "OPEN_AI_SEARCH_WIDGET",
      () => {
        onTapDetected();
      }
    );
    return () => {
      openAISearchWidget.remove();
    };
  }, []);

  // Screen dimensions - use shared values for worklet access
  const screenDimensions = Dimensions.get("window");
  const screenWidthValue = useSharedValue(screenDimensions.width);
  const screenHeightValue = useSharedValue(screenDimensions.height);

  // Boundaries as shared values for worklet access
  const minYValue = useSharedValue(insets.top + EDGE_MARGIN);
  const maxYValue = useSharedValue(
    screenDimensions.height - insets.bottom - BUTTON_HEIGHT - EDGE_MARGIN - 80
  );
  const leftEdgeXValue = useSharedValue(EDGE_MARGIN);
  const rightEdgeXValue = useSharedValue(
    screenDimensions.width - BUTTON_WIDTH - EDGE_MARGIN
  );

  // Update boundary values when insets change
  useEffect(() => {
    minYValue.value = insets.top + EDGE_MARGIN;
    maxYValue.value =
      screenHeightValue.value -
      insets.bottom -
      BUTTON_HEIGHT -
      EDGE_MARGIN -
      80;
  }, [insets.top, insets.bottom, minYValue, maxYValue, screenHeightValue]);

  // Get initial position
  const getInitialPosition = () => {
    if (savedPosition) {
      return savedPosition;
    }
    return {
      x: screenDimensions.width - BUTTON_WIDTH - EDGE_MARGIN,
      y:
        screenDimensions.height -
        insets.bottom -
        BUTTON_HEIGHT -
        EDGE_MARGIN -
        100,
    };
  };

  const initialPos = getInitialPosition();

  // Reanimated shared values
  const translateX = useSharedValue(initialPos.x);
  const translateY = useSharedValue(initialPos.y);
  const scale = useSharedValue(1);
  const opacity = useSharedValue(1);
  const contextX = useSharedValue(0);
  const contextY = useSharedValue(0);

  // JS thread functions for state updates
  const onPositionSaved = useCallback((x: number, y: number) => {
    setCurrentPosition({ x, y });
  }, []);

  const onTapDetected = useCallback(() => {
    if (isWeb) {
      setIsTooltipOpen(true);
    } else {
      navigation.navigate(Routes.AIChatBotScreen as never);
    }
    trackEvent(AIEventNames.AI_CHAT_WIDGET, {
      action: AIEventTypes.WIDGET_OPENED,
    });
  }, [navigation, isWeb]);

  const handleCloseTooltip = useCallback(() => {
    setIsTooltipOpen(false);
    trackEvent(AIEventNames.AI_CHAT_WIDGET, {
      action: AIEventTypes.WIDGET_CLOSED,
    });
  }, []);

  // Reset state on focus
  useFocusEffect(
    useCallback(() => {
      scale.value = withTiming(1, { duration: 100 });
      opacity.value = withTiming(1, { duration: 100 });
    }, [scale, opacity])
  );

  // Tap gesture for clicking
  const tapGesture = useMemo(() => {
    return Gesture.Tap().onEnd(() => {
      "worklet";
      runOnJS(onTapDetected)();
    });
  }, [onTapDetected]);

  // Pan gesture for dragging
  const panGesture = useMemo(() => {
    return Gesture.Pan()
      .minDistance(10)
      .onStart(() => {
        "worklet";
        contextX.value = translateX.value;
        contextY.value = translateY.value;
      })
      .onUpdate((event) => {
        "worklet";
        scale.value = withSpring(1.1, { damping: 15, stiffness: 200 });
        opacity.value = withTiming(0.85, { duration: 100 });

        translateX.value = contextX.value + event.translationX;
        translateY.value = contextY.value + event.translationY;
      })
      .onEnd(() => {
        "worklet";
        scale.value = withSpring(1, { damping: 15, stiffness: 200 });
        opacity.value = withTiming(1, { duration: 100 });

        // Snap to edge
        const snapX =
          translateX.value > screenWidthValue.value / 2
            ? rightEdgeXValue.value
            : leftEdgeXValue.value;
        const clampedY = Math.min(
          Math.max(translateY.value, minYValue.value),
          maxYValue.value
        );

        translateX.value = withSpring(snapX, { damping: 15, stiffness: 150 });
        translateY.value = withSpring(clampedY, {
          damping: 15,
          stiffness: 150,
        });

        // Save position on JS thread
        runOnJS(savePositionJS)(snapX, clampedY);
        runOnJS(onPositionSaved)(snapX, clampedY);
      })
      .onFinalize(() => {
        "worklet";
        scale.value = withSpring(1, { damping: 15, stiffness: 200 });
        opacity.value = withTiming(1, { duration: 100 });
      });
  }, [
    contextX,
    contextY,
    translateX,
    translateY,
    scale,
    opacity,
    screenWidthValue,
    rightEdgeXValue,
    leftEdgeXValue,
    minYValue,
    maxYValue,
    onPositionSaved,
  ]);

  // Combine gestures - tap and pan are exclusive (Race)
  const composedGesture = useMemo(() => {
    return Gesture.Race(tapGesture, panGesture);
  }, [tapGesture, panGesture]);

  // Animated style
  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        { translateX: translateX.value },
        { translateY: translateY.value },
        { scale: scale.value },
      ],
      opacity: opacity.value,
    };
  });

  const fixedStyle = useMemo(
    () => [
      styles.container,
      styles.fixedContainer,
      {
        right: fixedRightMargin,
        bottom: fixedBottomMargin,
        opacity: isTooltipOpen ? 0 : 1,
      },
    ],
    [fixedRightMargin, fixedBottomMargin, isTooltipOpen]
  );

  // Update dimensions on change
  useEffect(() => {
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      screenWidthValue.value = window.width;
      screenHeightValue.value = window.height;

      const newRightEdgeX = window.width - BUTTON_WIDTH - EDGE_MARGIN;
      const newMaxY =
        window.height - insets.bottom - BUTTON_HEIGHT - EDGE_MARGIN - 80;

      rightEdgeXValue.value = newRightEdgeX;
      maxYValue.value = newMaxY;

      if (savedPosition) {
        const clampedX =
          savedPosition.x > window.width / 2 ? newRightEdgeX : EDGE_MARGIN;
        const clampedY = Math.min(
          Math.max(savedPosition.y, insets.top + EDGE_MARGIN),
          newMaxY
        );
        translateX.value = clampedX;
        translateY.value = clampedY;
        savedPosition = { x: clampedX, y: clampedY };
      }
    });

    return () => subscription?.remove();
  }, [
    insets,
    translateX,
    translateY,
    screenWidthValue,
    screenHeightValue,
    rightEdgeXValue,
    maxYValue,
  ]);

  if (!shouldRender) {
    return null;
  }

  // Fixed (non-draggable) mode
  if (!isDraggable) {
    return (
      <>
        <Pressable
          onPress={onTapDetected}
          style={fixedStyle}
          pointerEvents={isTooltipOpen ? "none" : "auto"}
        >
          <AISearchButtonVisual
            typography={
              theme.isLargeScreen ? typography["H3-M"] : typography["Body-M"]
            }
          />
        </Pressable>

        {isWeb && (
          <ChatTooltip
            isOpen={isTooltipOpen}
            onClose={handleCloseTooltip}
            theme={theme}
          />
        )}
      </>
    );
  }

  return (
    <>
      <GestureDetector gesture={composedGesture}>
        <Animated.View
          style={[
            styles.container,
            animatedStyle,
            { opacity: isTooltipOpen ? 0 : undefined },
          ]}
          pointerEvents={isTooltipOpen ? "none" : "auto"}
        >
          <CoachOverlayStep
            screen="AISearchWidget"
            stepKey="widget"
            text={
              screenConfig?.steps?.[0]?.text || "Default product types overlay"
            }
            order={1}
          >
            <AISearchButtonVisual
              typography={
                theme.isLargeScreen ? typography["H3-M"] : typography["Body-M"]
              }
            />
          </CoachOverlayStep>
        </Animated.View>
      </GestureDetector>

      {isWeb && (
        <ChatTooltip
          isOpen={isTooltipOpen}
          onClose={handleCloseTooltip}
          theme={theme}
          position={currentPosition || undefined}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    zIndex: 9999,
    elevation: 10,
  },
  fixedContainer: {},
});

export default memo(FloatingChatBotButton);
